import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const FINISHES = ["Glossy", "Matte", "Polished", "Rustic", "Lappato", "Sugar", "Carving"];
const GRADES = ["Premium", "Standard", "Commercial"];
const SIZES = ["300x300mm", "300x600mm", "600x600mm", "600x1200mm", "800x800mm", "1200x1800mm"];

export default function DistributorCatalog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  function blank() {
    return { name: "", tile_size: SIZES[2], finish: FINISHES[0], batch_number: "", shade_code: "", grade: GRADES[1], boxes_available: 0, pallets_available: 0, price_per_box: 0, description: "" };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/distributor/catalog");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load catalog");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/distributor/catalog", { ...form, boxes_available: Number(form.boxes_available), pallets_available: Number(form.pallets_available), price_per_box: Number(form.price_per_box) });
      toast.success("Catalog item added");
      setOpen(false); setForm(blank()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/distributor/catalog/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="distributor-catalog">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Catalog &amp; inventory</h1>
          <p className="text-muted-foreground">Ceramic-native fields: size, finish, batch, shade, grade.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="dist-add-catalog-btn"><Plus className="w-4 h-4 mr-1" /> Add tile</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="dist-catalog-dialog">
            <DialogHeader><DialogTitle>New catalog item</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2"><Label>Product name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dist-catalog-name-input" /></div>
              <div className="space-y-2">
                <Label>Tile size</Label>
                <Select value={form.tile_size} onValueChange={(v) => setForm({ ...form, tile_size: v })}>
                  <SelectTrigger data-testid="dist-catalog-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Finish</Label>
                <Select value={form.finish} onValueChange={(v) => setForm({ ...form, finish: v })}>
                  <SelectTrigger data-testid="dist-catalog-finish-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FINISHES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Batch number</Label><Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="B-2026-031" data-testid="dist-catalog-batch-input" /></div>
              <div className="space-y-2"><Label>Shade / tone code</Label><Input value={form.shade_code} onChange={(e) => setForm({ ...form, shade_code: e.target.value })} placeholder="TC-04" data-testid="dist-catalog-shade-input" /></div>
              <div className="space-y-2">
                <Label>Grade</Label>
                <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                  <SelectTrigger data-testid="dist-catalog-grade-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{GRADES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Price / box</Label><Input type="number" value={form.price_per_box} onChange={(e) => setForm({ ...form, price_per_box: e.target.value })} data-testid="dist-catalog-price-input" /></div>
              <div className="space-y-2"><Label>Boxes available</Label><Input type="number" value={form.boxes_available} onChange={(e) => setForm({ ...form, boxes_available: e.target.value })} data-testid="dist-catalog-boxes-input" /></div>
              <div className="space-y-2"><Label>Pallets available</Label><Input type="number" value={form.pallets_available} onChange={(e) => setForm({ ...form, pallets_available: e.target.value })} data-testid="dist-catalog-pallets-input" /></div>
              <Button className="col-span-2" onClick={submit} data-testid="dist-catalog-submit-btn">Add to catalog</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="surface-card p-5 hover:-translate-y-1 hover:shadow-sm transition-all">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-overline">{c.grade}</div>
                <div className="font-display text-lg font-black mt-1">{c.name}</div>
              </div>
              <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`dist-delete-catalog-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
              <div><div className="label-overline">Size</div><div className="font-mono">{c.tile_size}</div></div>
              <div><div className="label-overline">Finish</div><div className="font-mono">{c.finish}</div></div>
              <div><div className="label-overline">Batch</div><div className="font-mono">{c.batch_number}</div></div>
              <div><div className="label-overline">Shade</div><div className="font-mono">{c.shade_code}</div></div>
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
              <div className="text-sm"><span className="font-mono text-foreground">{c.boxes_available}</span> <span className="text-muted-foreground">boxes</span></div>
              <div className="font-display font-bold text-primary">₹{Number(c.price_per_box).toLocaleString()}</div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground col-span-3 surface-card p-8 text-center" data-testid="dist-catalog-empty">No tiles yet. Add your first item.</div>}
      </div>
    </div>
  );
}
